/**
 * Organisation-level security policy stored on the Clerk organisation
 * `publicMetadata` so the browser guard can read it without an API round-trip.
 *
 * Supported shapes (mirrors the locations flag in `orgFeatures`):
 * - publicMetadata.requireMfa: boolean
 * - publicMetadata.security.requireMfa: boolean
 * - publicMetadata.allowDeviceTrustBypass: boolean
 * - publicMetadata.security.allowDeviceTrustBypass: boolean
 *
 * ASA-1527: a per-person bypass is recorded on membership and invitation
 * `publicMetadata.bypassClientTrust`. Clerk enforces it with the user field
 * `bypass_client_trust`, which applies to every organisation that person belongs to.
 */

/** Query string value used when the MFA guard redirects a user to the manage-MFA page. */
export const ORG_MFA_REDIRECT_REASON = "require-org-mfa";

/** Membership and invitation metadata key for a requested device-trust bypass. */
export const BYPASS_CLIENT_TRUST_METADATA_KEY = "bypassClientTrust";

interface ClerkOrgSecurityMetadata {
  requireMfa?: unknown;
  allowDeviceTrustBypass?: unknown;
  security?: { requireMfa?: unknown; allowDeviceTrustBypass?: unknown } | unknown;
}

function readOrgSecurityBoolean(
  publicMetadata: unknown,
  key: "requireMfa" | "allowDeviceTrustBypass",
): boolean | null {
  if (!publicMetadata || typeof publicMetadata !== "object") return null;
  const meta = publicMetadata as ClerkOrgSecurityMetadata;
  const topLevel = meta[key];
  if (typeof topLevel === "boolean") return topLevel;

  if (meta.security && typeof meta.security === "object") {
    const nested = (meta.security as { requireMfa?: unknown; allowDeviceTrustBypass?: unknown })[key];
    if (typeof nested === "boolean") return nested;
  }

  return null;
}

export function readRequireMfaFromClerkPublicMetadata(publicMetadata: unknown): boolean | null {
  return readOrgSecurityBoolean(publicMetadata, "requireMfa");
}

export function readAllowDeviceTrustBypassFromClerkPublicMetadata(
  publicMetadata: unknown,
): boolean | null {
  return readOrgSecurityBoolean(publicMetadata, "allowDeviceTrustBypass");
}

/** True only when membership or invitation metadata explicitly requests a bypass. */
export function readBypassClientTrustFromMetadata(publicMetadata: unknown): boolean {
  if (!publicMetadata || typeof publicMetadata !== "object") return false;
  return (
    (publicMetadata as Record<string, unknown>)[BYPASS_CLIENT_TRUST_METADATA_KEY] === true
  );
}
